import {
  getRelativePosition,
  useGetBibliography,
  useUpdateBibliography,
} from "@/api/bibliography/bibliography";
import AddFileModal from "@/components/modal/AddFileModal";
import { MaskableSeparator as Separator } from "@/components/form-items/MaskableSeparator";
import { Bibliography } from "@/lib/types";
import { useParams, useSearchParams } from "react-router-dom";

import { useGetAttachmentsByDocId } from "@/api/attachment";
import {
  appendNoDuplicateBibliographyNumbersRefine,
  bibliographySchema,
} from "@/api/bibliography/bibliography-validation";
import * as AF from "@/components/form-items/AbstractForm";
import { TwoDimensionalInput } from "@/components/form-items/DimensionsInput";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import LinksManagement from "@/components/LinksManagement";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { getMainLanguage } from "@/i18n";
import { FormLayout } from "@/layout/FormLayout";
import { formatFormRegistrationReference } from "@/lib/form-registration-header";
import { computeFieldLongSummaryBibliographie } from "@/lib/computeFields";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

const schema = appendNoDuplicateBibliographyNumbersRefine(
  bibliographySchema.passthrough(),
);

const EditBibliography = ({ bibliography }: { bibliography: Bibliography }) => {
  // Rafraîchit le tableau des fichiers après un ajout réussi dans AddFileModal.
  const [refreshFiles, setRefreshFiles] = useState(false);

  const { instanceData } = useAuth();
  const form = useForm({
    resolver: standardSchemaResolver(schema),
    reValidateMode: "onSubmit",
    defaultValues: bibliography,
  });

  const { data: attachments } = useGetAttachmentsByDocId(bibliography.id);
  const [, setSearchParam] = useSearchParams();

  const { t, i18n } = useTranslation();
  const vt = useVisibleT();

  const bibliographieDescriptionType = form.watch(
    "bibliographieDescriptionType",
  );
  const isPeriodicBibliography =
    bibliographieDescriptionType === "Périodique";

  const bibliographieDescriptionNomRevue = form.watch(
    "bibliographieDescriptionNomRevue",
  );
  const hasPeriodicalName =
    typeof bibliographieDescriptionNomRevue === "string" &&
    bibliographieDescriptionNomRevue.trim().length > 0;
  // Show the "include city & country in summary" checkbox for periodicals,
  // or as soon as a periodical name is filled in (even if the type isn't set).
  const showVillePaysDansResume = isPeriodicBibliography || hasPeriodicalName;

  const bibliographieDescriptionNumeroBibliographie = form.watch(
    "bibliographieDescriptionNumeroBibliographie",
  );
  const registrationLabel = t("backoffice.header.registrationNumber.label");
  const registrationReference = useMemo(
    () =>
      formatFormRegistrationReference(
        bibliographieDescriptionNumeroBibliographie,
        { label: registrationLabel, locale: i18n.language, suffix: bibliographieDescriptionType },
      ),
    [bibliographieDescriptionNumeroBibliographie, bibliographieDescriptionType, registrationLabel, i18n.language],
  );

  const biblioUpdater = useUpdateBibliography();

  const handleResume = (data: any) => {
    const result = computeFieldLongSummaryBibliographie(data, {
      language: getMainLanguage(),
    });
    return result;
  };

  const { relativePosition } = useDashboardParams<Bibliography>({
    statusField: "bibliographiePublicationStatus",
    resourceName: "bibliography",
    relativePosition: {
      currentId: bibliography.id,
      positionFetcher: getRelativePosition,
    },
  });

  const isModulePhotoEnabled = useMemo(() => {
    return instanceData?.isModulePhotoEnabled;
  }, [instanceData?.isModulePhotoEnabled]);

  const isModuleSculptureEnabled = useMemo(() => {
    return instanceData?.isModuleSculptureEnabled;
  }, [instanceData?.isModuleSculptureEnabled]);

  const queryClient = useQueryClient();

  return (
    <FormLayout
      formKey="bibliography"
      id={bibliography.id}
      title={bibliography.bibliographieDescriptionTitre}
      reference={registrationReference}
      publicationStatus={bibliography.bibliographiePublicationStatus}
      type="bibliography"
      relativePosition={relativePosition}
      picturesData={
        attachments && { pictures: attachments, type: "bibliography" }
      }
      invalidateOnChange={() => {
        queryClient.invalidateQueries({
          queryKey: ["bibliographies"],
        });
        queryClient.invalidateQueries({
          queryKey: ["bibliography", bibliography.id],
        });

        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: bibliography.id }],
        });
      }}
      onSave={(status) =>
        form.handleSubmit(
          (values) => {
            biblioUpdater.mutate(
              {
                ...values,
                ...(status && { bibliographiePublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("bibliographySavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (
              invalid.bibliographieDescriptionNumeroBibliographie != undefined
            )
              setSearchParam({ tab: "description" });
          },
        )()
      }
      form={form}
    >
      <Separator />
      <AF.AbstractForm
        form={form}
        formKey="bibliography"
        tabs={[
          { nonHideable: true,
            value: "description",
            label: t("bibliographyDescription"),
          },
          {
            value: "retranscription",
            label: t("retranscription"),
          },
          {
            value: "sources",
            label: t("sources"),
          },
          {
            value: "files",
            label: t("fichiers"),
          },
          {
            value: "links",
            label: t("linkedEntries"),
          },
        ]}
      >
        <AF.Tab value="description">
          <AF.Col>
            <AF.Text
              name="bibliographieDescriptionNumeroBibliographie"
              nonMaskable
            />
            <AF.Text name="bibliographieDescriptionAuteur" />
            <AF.Text className="w-16" name="bibliographieDescriptionAnnee" />
            <AF.Text name="bibliographieDescriptionTitre" nonMaskable />
            <AF.Text name="bibliographieDescriptionTitreDOrigine" />
            <ModifiableList
              name="bibliographieDescriptionLangueDOrigine"
              listKey="liste-langues"
              label={vt(
                "bibliographieDescriptionLangueDOrigine",
                "Langue d'origine",
              )}
            />
            <AF.Text name="bibliographieDescriptionSousTitre" />
            <AF.Row>
              <ModifiableList
                name="bibliographieDescriptionType"
                listKey="liste-types-biblio"
              />
              <AF.Checkbox
                align={true}
                name="bibliographieDescriptionIllustrations"
                className="pt-6"
              />
            </AF.Row>

            <AF.Text name="bibliographieDescriptionCollection" />
            <AF.Text name="bibliographieDescriptionEditeur" />
            <AF.Row>
              <ModifiableList
                name="bibliographieDescriptionVille"
                listKey="liste-villes"
              />
              <ModifiableList
                name="bibliographieDescriptionPays"
                listKey="liste-pays"
              />
            </AF.Row>
            <AF.Text name="bibliographieDescriptionNomRevue" />
            <AF.Checkbox
              name="bibliographieDescriptionInclureVillePaysDansResume"
              align={true}
              visible={showVillePaysDansResume}
              label={t(
                "bibliography.form.bibliographieDescriptionInclureVillePaysDansResume",
              )}
            />
            <AF.Row>
              <AF.Text name="bibliographieDescriptionNumeroRevue" />
              <AF.Text name="bibliographieDescriptionDatesPublication" />
            </AF.Row>
            <AF.Row>
              <AF.Text name="bibliographieDescriptionPageArticle" />
              <AF.Text name="bibliographieDescriptionNombrePages" />
            </AF.Row>
            <ModifiableList
              name="bibliographieDescriptionContexture"
              listKey="liste-description-biblio"
            />
            <ModifiableList
              name="bibliographieDescriptionCouverture"
              listKey="liste-description-biblio"
            />
            <AF.Row>
              <AF.Text name="bibliographieDescriptionIsbn" />
              <AF.Text name="bibliographieDescriptionAutreCodeBibliographie" />
            </AF.Row>
          </AF.Col>
          <AF.Col>
            <AF.Row>
              <AF.Checkbox name="bibliographieValidationReprosOeuvresOk" />
              <AF.Checkbox name="bibliographieValidationReprosPhotosOk" />
            </AF.Row>
            <AF.Row>
              <AF.Checkbox name="bibliographieValidationReprosDocumentsOk" />
              <AF.Checkbox name="bibliographieValidationListeDesOeuvresOk" />
            </AF.Row>
            <Separator />
            <AF.Row>
              <AF.Checkbox name="bibliographieValidationPublicationNumerisee" />
              <AF.Checkbox name="bibliographieValidationPdfEtOcrOk" />
            </AF.Row>
            <AF.Checkbox name="bibliographieValidationNoticeBibliographiqueOk" />
            <AF.RichText name="bibliographieDescriptionResumeCourt" />
            <AF.RichText
              name="bibliographieDescriptionResumeLong"
              computeAction={handleResume}
            />
            <AF.RichText name="bibliographieDescriptionNotes" />
            <ModifiableList
              name="bibliographieDescriptionLocalisationReference"
              listKey="liste-bibliotheques-archives"
            />
            <AF.Row>
              <AF.Text name="bibliographieDescriptionRangementReference1" />
              <AF.Text name="bibliographieDescriptionRangementReference2" />
            </AF.Row>
            <TwoDimensionalInput parentName="bibliographieDescriptionDimensions" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="retranscription">
          <AF.RichText name="bibliographieDescriptionRetranscription" />
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.RichText name="sources" />
        </AF.Tab>
        <AF.Tab
          isForm={false}
          value="files"
          headerExtras={
            <AddFileModal
              id={bibliography.id}
              onClose={() => {
                setRefreshFiles(true);
              }}
            />
          }
        >
          <AF.OthersFilesTable
            docId={bibliography.id}
            name="diversfiles"
            refreshFiles={refreshFiles}
            setRefreshFiles={setRefreshFiles}
          />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            id={bibliography.id}
            type="bibliography"
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "artworkInBibliography",
                      label: t("entryartworks"),
                      type: "link-complex",
                      fields: [
                        // Ligne 1 : Numéro biblio
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliography.bibliographieDescriptionNumeroBibliographie",
                              label: t(
                                "bibliographieDescriptionNumeroBibliographie",
                              ),
                              type: "text",
                              className: "w-full",
                              disabled: true,
                            },
                          ],
                        },
                        // Ligne 2 : Résumé biblio
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliography.bibliographieDescriptionResumeLong",
                              label: t("bibliographieDescriptionResumeLong"),
                              type: "richtext",
                              className: "w-full",
                              disabled: true,
                            },
                          ],
                        },
                        // Ligne 3 : Repr P. - Repr planche numéro
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "oeuvresDansBibliographieDescriptionReproduitPage",
                              label: t(
                                "oeuvresDansBibliographieDescriptionReproduitPage",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                            {
                              name: "oeuvresDansBibliographieDescriptionReproduitPlanche",
                              label: t(
                                "oeuvresDansBibliographieDescriptionReproduitPlanche",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        // Ligne 4 : Planche non numérotée
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full -mt-3 pt-0",
                          children: [
                            {
                              name: "oeuvresDansBibliographieDescriptionPlancheNonNumerotee",
                              label: t(
                                "oeuvresDansBibliographieDescriptionPlancheNonNumerotee",
                              ),
                              type: "checkbox",
                              className: "w-full col-start-2",
                            },
                          ],
                        },
                        // Ligne 5 : Cité page
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "oeuvresDansBibliographieDescriptionCitePage",
                              label: t(
                                "oeuvresDansBibliographieDescriptionCitePage",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        // Ligne 6 : Listé page - Sous le numéro
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "oeuvresDansBibliographieDescriptionListePage",
                              label: t(
                                "oeuvresDansBibliographieDescriptionListePage",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                            {
                              name: "oeuvresDansBibliographieDescriptionSousNumero",
                              label: t(
                                "oeuvresDansBibliographieDescriptionSousNumero",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        // Ligne 7 : Photographe
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "oeuvresDansBibliographieDescriptionPhotographe",
                              label: t(
                                "oeuvresDansBibliographieDescriptionPhotographe",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        // Ligne 8 : Non paginé - repr en couverture
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "oeuvresDansBibliographieDescriptionNonPagine",
                              label: t(
                                "oeuvresDansBibliographieDescriptionNonPagine",
                              ),
                              type: "checkbox",
                              className: "w-full",
                            },
                            {
                              name: "oeuvresDansBibliographieDescriptionReproductionCouverture",
                              label: t(
                                "oeuvresDansBibliographieDescriptionReproductionCouverture",
                              ),
                              type: "checkbox",
                              className: "w-full",
                            },
                          ],
                        },
                        // Ligne 9 : Repr couleur
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "oeuvresDansBibliographieDescriptionReproductionCouleur",
                              label: t(
                                "oeuvresDansBibliographieDescriptionReproductionCouleur",
                              ),
                              type: "checkbox",
                              className: "w-full",
                            },
                          ],
                        },
                        // Ligne 10 : Texte libre
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "oeuvresDansBibliographieDescriptionTexteLibre",
                              label: t(
                                "oeuvresDansBibliographieDescriptionTexteLibre",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        // Ligne 11 : Notes
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "oeuvresDansBibliographieNotes",
                              label: t("oeuvresDansBibliographieNotes"),
                              type: "richtext",
                              className: "w-full",
                            },
                          ],
                        },
                      ],
                    },
                    {
                      name: "multipleprintInBibliography",
                      label: t("entrymultipleprints"),
                      type: "link-complex",
                      fields: [
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliography.bibliographieDescriptionNumeroBibliographie",
                              label: t(
                                "bibliographieDescriptionNumeroBibliographie",
                              ),
                              type: "text",
                              className: "w-full",
                              disabled: true,
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliography.bibliographieDescriptionResumeLong",
                              label: t("bibliographieDescriptionResumeLong"),
                              type: "richtext",
                              className: "w-full",
                              disabled: true,
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionReproduitPage",
                              type: "text",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionReproduitPage",
                              ),
                              className: "w-full",
                            },
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionReproduitPlanche",
                              type: "text",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionReproduitPlanche",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full -mt-3 pt-0",
                          children: [
                            {
                              name: "biblioInMultiPrintDescPlancheNonNum",
                              type: "checkbox",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionPlancheNonNumerotee",
                              ),
                              className: "w-full col-start-2",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionCitePage",
                              type: "text",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionCitePage",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionListePage",
                              type: "text",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionListePage",
                              ),
                              className: "w-full",
                            },
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionSousNumero",
                              type: "text",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionSousNumero",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionPhotographe",
                              type: "text",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionPhotographe",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionNonPagine",
                              type: "checkbox",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionNonPagine",
                              ),
                              className: "w-full",
                            },
                            {
                              name: "biblioInMultiPrintDescReproCover",
                              type: "checkbox",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionReproductionCouverture",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionReproductionCoul",
                              type: "checkbox",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionReproductionCoul",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionExemplaireRepr",
                              type: "text",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionExemplaireRepr",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliographieDansEstampesMultiplesDescriptionTexteLibre",
                              type: "text",
                              label: t(
                                "bibliographieDansEstampesMultiplesDescriptionTexteLibre",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                      ],
                    },
                    {
                      name: "sculptureEditionInBibliography",
                      label: t("linkedSculptureEditions"),
                      type: "link-complex",
                      hidden: !isModuleSculptureEnabled,
                      fields: [
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliography.bibliographieDescriptionNumeroBibliographie",
                              label: t(
                                "bibliographieDescriptionNumeroBibliographie",
                              ),
                              type: "text",
                              className: "w-full",
                              disabled: true,
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliography.bibliographieDescriptionResumeLong",
                              label: t("bibliographieDescriptionResumeLong"),
                              type: "richtext",
                              className: "w-full",
                              disabled: true,
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "sculptureEditionDansBibliographieReproduitPage",
                              label: t(
                                "sculptureEditionDansBibliographieReproduitPage",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                            {
                              name: "sculptureEditionDansBibliographieReproduitPlanche",
                              label: t(
                                "sculptureEditionDansBibliographieReproduitPlanche",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full -mt-3 pt-0",
                          children: [
                            {
                              name: "sculptureEditionDansBibliographiePlancheNonNumerotee",
                              label: t(
                                "sculptureEditionDansBibliographiePlancheNonNumerotee",
                              ),
                              type: "checkbox",
                              className: "w-full col-start-2",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "sculptureEditionDansBibliographieCitePage",
                              label: t(
                                "sculptureEditionDansBibliographieCitePage",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "sculptureEditionDansBibliographieListePage",
                              label: t(
                                "sculptureEditionDansBibliographieListePage",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                            {
                              name: "sculptureEditionDansBibliographieSousNumero",
                              label: t(
                                "sculptureEditionDansBibliographieSousNumero",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "sculptureEditionDansBibliographiePhotographe",
                              label: t(
                                "sculptureEditionDansBibliographiePhotographe",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "sculptureEditionDansBibliographieNonPagine",
                              label: t(
                                "sculptureEditionDansBibliographieNonPagine",
                              ),
                              type: "checkbox",
                              className: "w-full",
                            },
                            {
                              name: "sculptureEditionDansBibliographieReproductionCouverture",
                              label: t(
                                "sculptureEditionDansBibliographieReproductionCouverture",
                              ),
                              type: "checkbox",
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "sculptureEditionDansBibliographieReproductionCouleur",
                              label: t(
                                "sculptureEditionDansBibliographieReproductionCouleur",
                              ),
                              type: "checkbox",
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "sculptureEditionDansBibliographieTexteLibre",
                              label: t(
                                "sculptureEditionDansBibliographieTexteLibre",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "sculptureEditionDansBibliographieNotes",
                              label: t(
                                "sculptureEditionDansBibliographieNotes",
                              ),
                              type: "richtext",
                              className: "w-full",
                            },
                          ],
                        },
                      ],
                    },
                    {
                      name: "multipleSampleInBibliography",
                      label: t("entrymultiplesamples"),
                      type: "link-complex",
                      fields: [
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliography.bibliographieDescriptionNumeroBibliographie",
                              label: t(
                                "bibliographieDescriptionNumeroBibliographie",
                              ),
                              type: "text",
                              className: "w-full",
                              disabled: true,
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliography.bibliographieDescriptionResumeLong",
                              label: t("bibliographieDescriptionResumeLong"),
                              type: "richtext",
                              className: "w-full",
                              disabled: true,
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "multiplesampleDansBibliographieDescriptionReproduitPage",
                              type: "text",
                              label: t(
                                "multiplesampleDansBibliographieDescriptionReproduitPage",
                              ),
                              className: "w-full",
                            },
                            {
                              name: "multiplesampleDansBibliographieDescriptionReproduitPlanche",
                              type: "text",
                              label: t(
                                "multiplesampleDansBibliographieDescriptionReproduitPlanche",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full -mt-3 pt-0",
                          children: [
                            {
                              name: "multiplesampleDansBibliographieDescriptionPlancheNonNumerotee",
                              type: "checkbox",
                              label: t(
                                "multiplesampleDansBibliographieDescriptionPlancheNonNumerotee",
                              ),
                              className: "w-full col-start-2",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "multiplesampleDansBibliographieDescriptionCitePage",
                              type: "text",
                              label: t(
                                "multiplesampleDansBibliographieDescriptionCitePage",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "multiplesampleDansBibliographieDescriptionListePage",
                              type: "text",
                              label: t(
                                "multiplesampleDansBibliographieDescriptionListePage",
                              ),
                              className: "w-full",
                            },
                            {
                              name: "multiplesampleDansBibliographieDescriptionSousNumero",
                              type: "text",
                              label: t(
                                "multiplesampleDansBibliographieDescriptionSousNumero",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "multiplesampleDansBibliographieDescriptionPhotographe",
                              type: "text",
                              label: t(
                                "multiplesampleDansBibliographieDescriptionPhotographe",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "multiplesampleDansBibliographieDescriptionNonPagine",
                              type: "checkbox",
                              label: t(
                                "multiplesampleDansBibliographieDescriptionNonPagine",
                              ),
                              className: "w-full",
                            },
                            {
                              name: "multiSampleInBiblioDescReproCover",
                              type: "checkbox",
                              label: t(
                                "multiplesampleDansBibliographieDescriptionReproductionCouverture",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "multiplesampleDansBibliographieDescriptionReproductionCouleur",
                              type: "checkbox",
                              label: t(
                                "multiplesampleDansBibliographieDescriptionReproductionCouleur",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "multiplesampleDansBibliographieDescriptionTexteLibre",
                              type: "text",
                              label: t(
                                "multiplesampleDansBibliographieDescriptionTexteLibre",
                              ),
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "multiplesampleDansBibliographieNotes",
                              type: "richtext",
                              label: t("multiplesampleDansBibliographieNotes"),
                              className: "w-full",
                            },
                          ],
                        },
                      ],
                    },
                    {
                      name: "tirageInBibliography",
                      label: t("entrytirages"),
                      type: "link-complex",
                      hidden: !isModulePhotoEnabled,
                      fields: [
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliography.bibliographieDescriptionNumeroBibliographie",
                              label: t(
                                "bibliographieDescriptionNumeroBibliographie",
                              ),
                              type: "text",
                              className: "w-full",
                              disabled: true,
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliography.bibliographieDescriptionResumeLong",
                              label: t("bibliographieDescriptionResumeLong"),
                              type: "richtext",
                              className: "w-full",
                              disabled: true,
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "tirageDansBibliographieDescriptionReproduitPage",
                              label: t(
                                "tirageDansBibliographieDescriptionReproduitPage",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                            {
                              name: "tirageDansBibliographieDescriptionReproduitPlanche",
                              label: t(
                                "tirageDansBibliographieDescriptionReproduitPlanche",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full -mt-3 pt-0",
                          children: [
                            {
                              name: "tirageDansBibliographieDescriptionPlancheNonNumerotee",
                              label: t(
                                "tirageDansBibliographieDescriptionPlancheNonNumerotee",
                              ),
                              type: "checkbox",
                              className: "w-full col-start-2",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "tirageDansBibliographieDescriptionCitePage",
                              label: t(
                                "tirageDansBibliographieDescriptionCitePage",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "tirageDansBibliographieDescriptionListePage",
                              label: t(
                                "tirageDansBibliographieDescriptionListePage",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                            {
                              name: "tirageDansBibliographieDescriptionSousNumero",
                              label: t(
                                "tirageDansBibliographieDescriptionSousNumero",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "tirageDansBibliographieDescriptionPhotographe",
                              label: t(
                                "tirageDansBibliographieDescriptionPhotographe",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "tirageDansBibliographieDescriptionNonPagine",
                              label: t(
                                "tirageDansBibliographieDescriptionNonPagine",
                              ),
                              type: "checkbox",
                              className: "w-full",
                            },
                            {
                              name: "tirageDansBibliographieDescriptionReproductionCouverture",
                              label: t(
                                "tirageDansBibliographieDescriptionReproductionCouverture",
                              ),
                              type: "checkbox",
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "tirageDansBibliographieDescriptionReproductionCouleur",
                              label: t(
                                "tirageDansBibliographieDescriptionReproductionCouleur",
                              ),
                              type: "checkbox",
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "tirageDansBibliographieDescriptionTexteLibre",
                              label: t(
                                "tirageDansBibliographieDescriptionTexteLibre",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "tirageDansBibliographieNotes",
                              label: t("tirageDansBibliographieNotes"),
                              type: "richtext",
                              className: "w-full",
                            },
                          ],
                        },
                      ],
                    },
                    {
                      name: "relatedExhibition",
                      label: t("entryexhibitions"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAuction",
                      label: t("entryauctions"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAuthor",
                      label: t("entryauthors"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAudiovisual",
                      label: t("entryaudiovisuals"),
                      type: "link-simple",
                    },
                    {
                      name: "documentInBibliography",
                      label: t("entrydocuments"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "documentsDansBibliographieDescriptionReproduitPage",
                          type: "text",
                          label: t(
                            "documentsDansBibliographieDescriptionReproduitPage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "documentsDansBibliographieDescriptionCitePage",
                          type: "text",
                          label: t(
                            "documentsDansBibliographieDescriptionCitePage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "documentsDansBibliographieDescriptionPhotographe",
                          type: "text",
                          label: t(
                            "documentsDansBibliographieDescriptionPhotographe",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "documentsDansBibliographieDescriptionReproductionCouleur",
                          type: "checkbox",
                          label: t(
                            "documentsDansBibliographieDescriptionReproductionCouleur",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "documentsDansBibliographieDescriptionNonPagine",
                          type: "checkbox",
                          label: t(
                            "documentsDansBibliographieDescriptionNonPagine",
                          ),
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      // Lien complexe Biblio ↔ Biblio (bidirectionnel).
                      name: "bibliographyInBibliography",
                      label: t("entrybibliographies"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "bibliographieDansBibliographieDescriptionPage",
                          type: "text",
                          label: t(
                            "bibliographieDansBibliographieDescriptionPage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "bibliographieDansBibliographieDescriptionReproduitPage",
                          type: "text",
                          label: t(
                            "bibliographieDansBibliographieDescriptionReproduitPage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "bibliographieDansBibliographieDescriptionCitePage",
                          type: "text",
                          label: t(
                            "bibliographieDansBibliographieDescriptionCitePage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "bibliographieDansBibliographieDescriptionListePage",
                          type: "text",
                          label: t(
                            "bibliographieDansBibliographieDescriptionListePage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "bibliographieDansBibliographieDescriptionNonPagine",
                          type: "checkbox",
                          label: t(
                            "bibliographieDansBibliographieDescriptionNonPagine",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "bibliographieDansBibliographieDescriptionReproductionCouverture",
                          type: "checkbox",
                          label: t(
                            "bibliographieDansBibliographieDescriptionReproductionCouverture",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "bibliographieDansBibliographieDescriptionReproductionCouleur",
                          type: "checkbox",
                          label: t(
                            "bibliographieDansBibliographieDescriptionReproductionCouleur",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "bibliographieDansBibliographieDescriptionTextePersonnalise",
                          type: "text",
                          expandOnOverflow: true,
                          label: t(
                            "bibliographieDansBibliographieDescriptionTextePersonnalise",
                          ),
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "relatedWriting",
                      label: t("entrywritings"),
                      type: "link-simple",
                    },
                    {
                      name: "photographyInBibliography",
                      label: t("entryphotographies"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "photographiesDansBibliographieDescriptionReproduitPage",
                          type: "text",
                          label: t(
                            "photographiesDansBibliographieDescriptionReproduitPage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "photographiesDansBibliographieDescriptionCitePage",
                          type: "text",
                          label: t(
                            "photographiesDansBibliographieDescriptionCitePage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "photographiesDansBibliographieDescriptionPhotographe",
                          type: "text",
                          label: t(
                            "photographiesDansBibliographieDescriptionPhotographe",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "photographiesDansBibliographieDescriptionReproductionCouleur",
                          type: "checkbox",
                          label: t(
                            "photographiesDansBibliographieDescriptionReproductionCouleur",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "photographiesDansBibliographieDescriptionNonPagine",
                          type: "checkbox",
                          label: t(
                            "photographiesDansBibliographieDescriptionNonPagine",
                          ),
                          className: "w-full",
                        },
                      ],
                    },
                  ],
                },
              ],
            }}
            data={bibliography}
          />
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditBibliographyPage = () => {
  const { id } = useParams();

  const { data, isLoading } = useGetBibliography(id);

  return (
    <>
      {data && <EditBibliography key={data.id} bibliography={data} />}
      {!data && !isLoading && (
        <h2 className="p-4">bibliographieNexistePasLabel</h2>
      )}
    </>
  );
};
